import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import agentsHeroImage from "../../assets/area/Ajmal.jpg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const formatAED = (value) =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format(value || 0);

const toNumber = (v) => Number(v?.toString().replace(/,/g, "")) || 0;

const formatNumberInput = (value) => {
  const numeric = value.replace(/[^0-9]/g, "");
  return numeric ? Number(numeric).toLocaleString("en-US") : "";
};

export default function RoiHeroSection() {
  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "var(--color-bg)",
        overflow: "hidden",
        pt: { xs: 14, md: 18 }, // ✅ FIX mobile top cut
        pb: { xs: 8, md: 14 },
      }}
    >
      {/* BACKGROUND IMAGE / TEXTURE */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,

          /* Background image */
          backgroundImage: `url(${agentsHeroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",

          /* Overlay layer */
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: {
              xs: `
          linear-gradient(
            180deg,
            rgba(255,255,255,0.96) 0%,
            rgba(255,255,255,0.85) 35%,
            rgba(255,255,255,0.55) 60%,
            rgba(255,255,255,0.25) 78%,
            rgba(255,255,255,0.05) 92%
          )
        `,
              md: `
          linear-gradient(
            90deg,
            rgba(255,255,255,0.95) 0%,
            rgba(255,255,255,0.85) 30%,
            rgba(255,255,255,0.55) 52%,
            rgba(255,255,255,0.22) 72%,
            rgba(255,255,255,0.05) 88%
          )
        `,
            },

            /* 👇 KEY PART */
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 10 }}>
          {/* LEFT CONTENT */}
          <Grid item xs={12} md={6}>
            <Typography
              sx={{
                fontSize: { xs: 26, sm: 30, md: 44 },
                fontWeight: 400,
                color: "var(--color-text-primary)",
                mb: 1,
              }}
            >
              Your Property Investment
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 34, sm: 38, md: 56 },
                fontWeight: 700,
                lineHeight: 1.1,
                color: "var(--color-primary-dark)",
                mb: 3,
              }}
            >
              <Box component="span" sx={{ color: "var(--color-gold)" }}>
                ROI
              </Box>{" "}
              Calculator
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                lineHeight: 1.8,
                color: "var(--color-text-muted)",
                maxWidth: 520,
              }}
            >
              Investing in real estate can generate excellent returns — but
              understanding exactly how much matters. Use our ROI calculator to
              estimate annual returns by factoring in rental income, expenses,
              and financing.
            </Typography>

            <Typography
              sx={{
                mt: 3,
                fontSize: 13,
                color: "var(--color-text-muted)",
              }}
            >
              * This calculator provides estimates only and does not constitute
              financial advice.
            </Typography>
            <Box
              sx={{
                mt: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
                gap: 5,
                pt: 2,
                borderTop: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              {/* Insight 1 */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: "var(--color-primary-dark)",
                    lineHeight: 1,
                  }}
                >
                  <Box component="span" sx={{ color: "var(--color-gold)" }}>
                    6–10%
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "var(--color-text-muted)",
                    mt: 0.5,
                    letterSpacing: "0.04em",
                  }}
                >
                  Typical Net ROI
                </Typography>
              </Box>

              {/* Vertical Divider */}
              <Box
                sx={{
                  width: "1px",
                  height: 40,
                  backgroundColor: "rgba(0,0,0,0.12)",
                }}
              />

              {/* Insight 2 */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: "var(--color-primary-dark)",
                    lineHeight: 1,
                  }}
                >
                  Sharjah
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "var(--color-text-muted)",
                    mt: 0.5,
                    letterSpacing: "0.04em",
                  }}
                >
                  UAE Real Estate Focus
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT FORM */}
          <Grid item xs={12} md={6} sx={{ margin: "auto" }}>
            <RoiCalculatorCard />
          </Grid>
        </Grid>
      </Container>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, var(--color-gold), transparent)",
          opacity: 0.6,
        }}
      />
    </Box>
  );
}

function RoiCalculatorCard() {
  const [values, setValues] = React.useState({
    price: "",
    rent: "",
    expenses: "",
    loan: "",
    rate: "",
  });

  const [results, setResults] = React.useState(null);
  const [showResults, setShowResults] = React.useState(false);

  const handleChange = (key) => (e) => {
    const formattedValue =
      key === "rate"
        ? e.target.value.replace(/[^0-9.]/g, "")
        : formatNumberInput(e.target.value);

    setValues({ ...values, [key]: formattedValue });
  };

  const calculateROI = () => {
    const price = toNumber(values.price);
    const rent = toNumber(values.rent);
    const expenses = toNumber(values.expenses);
    const loan = toNumber(values.loan);
    const rate = toNumber(values.rate);

    const annualLoanCost = loan * (rate / 100);
    const netIncome = rent - expenses;
    const cashFlow = netIncome - annualLoanCost;

    const netYield = price ? (netIncome / price) * 100 : 0;

    const roi = loan
      ? (cashFlow / (price - loan)) * 100
      : (netIncome / price) * 100;

    setResults({
      annualLoanCost,
      netIncome,
      cashFlow,
      rentalYield: netYield,
      roi,
    });
    setShowResults(true);
  };

  const isFormComplete =
    values.price &&
    values.rent &&
    values.expenses &&
    values.loan &&
    values.rate;


  return (
    <Box
      sx={{
        backgroundColor: "var(--color-white)",
        borderRadius: "18px",
        p: { xs: 3, sm: 4 },
        boxShadow: "0 30px 80px rgba(0,0,0,0.14)",
        border: "1px solid rgba(0,0,0,0.06)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          letterSpacing: "0.18em",
          color: "var(--color-text-muted)",
          mb: 1,
        }}
      >
        SHARJAH REAL ESTATE
      </Typography>

      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 600,
          color: "var(--color-text-primary)",
          mb: 1,
        }}
      >
        ROI Investment Calculator
      </Typography>
      <Box
        sx={{
          height: 2,
          width: 40,
          backgroundColor: "var(--color-gold)",
          mb: 3,
        }}
      />

      {!showResults && (
        <>
          <Grid
            container
            spacing={3}
            sx={{
              width: "100%", // force full width
              mx: "auto", // center container
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: "auto" },
              }}
            >
              <LuxuryInput
                label="Property Price (AED)"
                value={values.price}
                onChange={handleChange("price")}
                placeholder="1,000,000"
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: "auto" },
              }}
            >
              <LuxuryInput
                label="Expected Annual Rent (AED)"
                value={values.rent}
                onChange={handleChange("rent")}
                placeholder="50,000"
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: "auto" },
              }}
            >
              <LuxuryInput
                label="Annual Expenses (AED)"
                value={values.expenses}
                onChange={handleChange("expenses")}
                placeholder="10,000"
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: "auto" },
              }}
            >
              <LuxuryInput
                label="Loan Amount (Optional, AED)"
                value={values.loan}
                onChange={handleChange("loan")}
                placeholder="600,000"
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0 },
              }}
            >
              <LuxuryInput
                label="Annual Interest Rate (%)"
                value={values.rate}
                onChange={handleChange("rate")}
                placeholder="4"
              />
            </Grid>
          </Grid>

          <Button
            fullWidth
            onClick={calculateROI}
            disabled={!isFormComplete}
            sx={{
              mt: 4,
              py: 1.6,
              borderRadius: "10px",
              letterSpacing: "0.15em",
              fontWeight: 500,
              transition: "all 0.25s ease",

              /* ✅ NORMAL STATE */
              backgroundColor: "var(--color-primary-dark)",
              color: "var(--color-white)",

              /* ✅ HOVER (only when enabled) */
              "&:hover": {
                backgroundColor: isFormComplete
                  ? "var(--color-gold)"
                  : "var(--color-primary-dark)",
                color: isFormComplete ? "#000" : "var(--color-white)",
              },

              /* ✅ DISABLED STATE — THIS FIXES YOUR ISSUE */
              "&.Mui-disabled": {
                backgroundColor: "rgba(0,0,0,0.08)", // light neutral
                color: "rgba(0,0,0,0.35)",
                cursor: "not-allowed",
                boxShadow: "none",
                opacity: 1, // 🔥 VERY IMPORTANT (prevents black overlay)
              },
            }}
          >
            CALCULATE ROI
          </Button>
        </>
      )}

      {showResults && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            mb: { xs: 2, sm: 1.5 },
            cursor: "pointer",
            color: "var(--color-text-muted)",
            width: "fit-content",
            transition: "color 0.25s ease",

            "&:hover": {
              color: "var(--color-gold)",
            },
          }}
          onClick={() => {
            setShowResults(false);
            setResults(null);
          }}
        >
          {/* Icon button */}
          <Box
            sx={{
              width: { xs: 40, sm: 36 }, // larger tap area on mobile
              height: { xs: 40, sm: 36 },
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.25s ease",

              "&:hover": {
                backgroundColor: "var(--color-gold)",
                color: "var(--color-white)",
                borderColor: "var(--color-gold)",
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </Box>

          {/* Label */}
          <Typography
            sx={{
              fontSize: { xs: 12, sm: 13, md: 14 },
              letterSpacing: "0.12em",
              lineHeight: 1,
              userSelect: "none",

              "&:hover": {
                color: "var(--color-gold)",
              },
            }}
          >
            BACK TO CALCULATE
          </Typography>
        </Box>
      )}

      {results && (
        <Box
          sx={{
            // p: { xs: 3, md: 5 },
            // borderRadius: "20px",
            // backgroundColor: "rgba(255,255,255,0.95)",
            // boxShadow: "0 40px 100px rgba(0,0,0,0.15)",
            width: "100%", // force full width
            mx: "auto", // center container
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <FinancialOverviewChart
            data={{
              rent: toNumber(values.rent),
              expenses: toNumber(values.expenses),
              annualLoanCost: results.annualLoanCost,
              netIncome: results.netIncome,
              cashFlow: results.cashFlow,
            }}
          />

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 14,
              letterSpacing: "0.18em",
              color: "var(--color-text-muted)",
              mb: 4,
            }}
          >
            INVESTMENT ANALYSIS
          </Typography>

          <Grid
            container
            sx={{
              width: "100%", // force full width
              mx: "auto", // center container
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <ResultBox
              title="Net Annual Income"
              value={formatAED(results.netIncome)}
            />
            <ResultBox
              title="Annual Loan Cost"
              value={formatAED(results.annualLoanCost)}
            />
            <ResultBox
              title="Net Cash Flow"
              value={formatAED(results.cashFlow)}
            />
            <ResultBox
              title="Return on Investment"
              value={`${results.roi.toFixed(2)}%`}
              highlight
            />
            <ResultBox
              title="Rental Yield"
              value={`${results.rentalYield.toFixed(2)}%`}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
}

function LuxuryInput({ label, value, onChange, placeholder }) {
  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{ fontSize: 12, color: "var(--color-text-muted)", mb: 0.5 }}
      >
        {label}
      </Typography>

      <TextField
        fullWidth
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        variant="outlined"
        InputProps={{
          sx: {
            height: 52,
            borderRadius: "12px",
            backgroundColor: "#fafafa",
            "& input::placeholder": {
              color: "#9e9e9e",
              opacity: 1,
            },
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&:hover fieldset": {
              borderColor: "var(--color-gold)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--color-gold)",
              boxShadow: "0 0 0 3px rgba(207,172,106,0.15)",
            },
          },
        }}
      />
    </Box>
  );
}

function ResultBox({ title, value, highlight }) {
  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={6}
      sx={{
        width: { xs: "100%", md: "auto" },
        mx: { xs: 0, md: "auto" },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 5,
          textAlign: "center",
          height: "100%",
          backgroundColor: highlight
            ? "var(--color-primary-dark)"
            : "var(--color-white)",
          color: highlight ? "var(--color-white)" : "var(--color-text-primary)",
          border: "1px solid rgba(0,0,0,0.12)",
          transition: "all 0.3s ease",
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: "0.18em",
            color: highlight ? "var(--color-white)" : "var(--color-text-muted)",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 22, md: 20 },
            fontWeight: 500,
            color: highlight ? "var(--color-gold)" : "inherit",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Grid>
  );
}

function FinancialOverviewChart({ data }) {
  const chartData = [
    { name: "Annual Rent", value: data.rent, type: "highlight" },
    { name: "Expenses", value: data.expenses, type: "muted" },
    { name: "Loan Cost", value: data.annualLoanCost, type: "light" },
    { name: "Net Income", value: data.netIncome, type: "highlight" },
    { name: "Cash Flow", value: data.cashFlow, type: "muted" },
  ];

  // 🔢 Calculate dynamic Y-axis max
  const maxValue = Math.max(...chartData.map((d) => d.value));
  const roundedMax = Math.ceil(maxValue / 5000) * 5000;

  const barColor = (type) => {
    switch (type) {
      case "highlight":
        return "var(--color-gold)";
      case "muted":
        return "rgba(0,0,0,0.55)";
      case "light":
        return "rgba(0,0,0,0.35)";
      default:
        return "var(--color-primary-dark)";
    }
  };

  return (
    <Box sx={{ mb: 8 }}>
      <Box
        sx={{
          textAlign: "center",
          mb: { xs: 4, sm: 5, md: 6 },
          px: { xs: 2, sm: 0 }, // prevent edge cut on mobile
        }}
      >
        {/* Small kicker */}
        <Typography
          sx={{
            fontSize: { xs: 11, sm: 12 },
            letterSpacing: { xs: "0.22em", sm: "0.28em" },
            color: "var(--color-text-muted)",
            mb: 1,
          }}
        >
          FINANCIAL OVERVIEW
        </Typography>

        {/* Main sub-heading */}
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 20, md: 22 },
            fontWeight: 600,
            color: "var(--color-text-primary)",
            lineHeight: 1.35,
            letterSpacing: "0.04em",
            maxWidth: 560,
            mx: "auto",
          }}
        >
          Your Comprehensive ROI Report
        </Typography>

        {/* Accent divider */}
        <Box
          sx={{
            width: { xs: 36, sm: 48 },
            height: 2,
            backgroundColor: "var(--color-gold)",
            mx: "auto",
            mt: { xs: 1.5, sm: 2 },
          }}
        />
      </Box>

      <Box sx={{ width: "100%", height: 320 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} barSize={48}>
            <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} />

            <YAxis
              domain={[0, roundedMax]}
              tickCount={Math.min(roundedMax / 5000 + 1, 8)}
              tickFormatter={(v) => `AED ${v.toLocaleString()}`}
              tick={{ fontSize: 10 }}
              axisLine={false}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
            />

            <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={barColor(entry.type)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}


function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box
      sx={{
        backgroundColor: "var(--color-primary-dark)",
        color: "var(--color-white)",
        px: 2,
        py: 1.5,
        borderRadius: "8px",
        boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          letterSpacing: "0.08em",
          opacity: 0.85,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--color-gold)",
        }}
      >
        {formatAED(payload[0].value)}
      </Typography>
    </Box>
  );
}
